import { OneStemBrand } from '@/components/brand/OneStemBrand'
import { getAdminBuildUpdateMetadata, type FetchLatestAdminBuildMetadata } from '@/lib/admin-build'
import { getErrorMessage, readProperty } from '@/lib/utils'
import { RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from './ui/button'
import { Typography } from '@/components/ui/typography'
function reloadPage() {
  window.location.reload()
}

function isPotentialStaleBuildError(error: unknown) {
  // oxlint-disable-next-line typescript/restrict-template-expressions, typescript/no-base-to-string -- Preserve legacy implicit name coercion at the unknown error boundary.
  const errorText = `${readProperty(error, 'name') || ''} ${getErrorMessage(error)}`.toLowerCase()

  return [
    'chunkloaderror',
    'loading chunk',
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'importing a module script failed',
    'unable to preload css',
  ].some((staleBuildErrorText) => errorText.includes(staleBuildErrorText))
}

export default function DefaultError({
  error,
  currentBuildId,
  fetchLatestBuildMetadata,
}: {
  error: unknown
  currentBuildId?: string | null
  fetchLatestBuildMetadata?: FetchLatestAdminBuildMetadata
}) {
  const [hasNewerBuild, setHasNewerBuild] = useState(false)
  const shouldCheckForNewerBuild = isPotentialStaleBuildError(error)

  useEffect(() => {
    if (!shouldCheckForNewerBuild) {
      return undefined
    }

    let isMounted = true

    void getAdminBuildUpdateMetadata({
      currentBuildId,
      fetchLatest: fetchLatestBuildMetadata,
    }).then((metadata) => {
      if (isMounted && metadata) {
        setHasNewerBuild(true)
      }
    })

    return () => {
      isMounted = false
    }
  }, [currentBuildId, fetchLatestBuildMetadata, shouldCheckForNewerBuild])

  if (hasNewerBuild) {
    return (
      <div className="flex h-screen mx-auto max-w-2xl flex-col items-center justify-center gap-y-4 px-6 text-center">
        <div className="flex items-center gap-x-4">
          <OneStemBrand variant="icon" className="size-12 flex-none" />
          <Typography variant="h1">Updates are ready</Typography>
        </div>
        <Typography variant="h3">We've made some updates.</Typography>
        <Typography variant="bodyLarge" className="max-w-xl">
          Refresh this page to see the latest changes.
        </Typography>
        <Button onClick={reloadPage}>
          <RefreshCw />
          Refresh page
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-4 h-screen items-center justify-center mx-auto max-w-2xl">
      <div className="flex items-center gap-x-4">
        <OneStemBrand variant="icon" className="size-12 flex-none" />
        <Typography variant="h1">Uh oh</Typography>
      </div>
      <Typography variant="h3">An error has occurred.</Typography>
      <Typography variant="inlineCode">Error: {getErrorMessage(error)}</Typography>
      <Button asChild>
        {/* Reload the document and let login recover the session's valid organization. */}
        <a href="/login">Refresh</a>
      </Button>
    </div>
  )
}
